// Single source of truth for /tools/find-housing-counselor.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/find-housing-counselor";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "Find a Free, HUD-Approved Housing Counselor Near You";
export const PAGE_DESCRIPTION =
  "How to find free HUD-approved housing counseling by ZIP code, phone, language, or specialty — what is actually free, which hotline to call today, and the scam red flags to screen for first.";

export const SECTIONS = [
  { id: "free-vs-fee", title: "What's free and what isn't" },
  { id: "search-online", title: "Search online by ZIP code" },
  { id: "call-now", title: "Call now if you need help today" },
  { id: "specialties", title: "Specialty and language filters" },
  { id: "scams", title: "Screen for scams before you sign" },
  { id: "faq", title: "Common questions" },
];

export const HERO_PROMISE =
  "HUD-approved agencies give free or low-cost guidance on buying, renting, paying your mortgage, and avoiding foreclosure. Here are the three ways to find one — by ZIP code, by phone, or by specialty — and how to confirm the agency is real before you share a single document.";

export const CTA = {
  label: "Find counseling by ZIP code",
  href: "https://www.hud.gov/i_want_to/talk_to_a_housing_counselor",
  resource: "hud_agency_locator",
};

export const FREE_VS_FEE = {
  heading: "Free vs. fee-based counseling, in plain language",
  bullets: [
    {
      label: "Almost always free",
      text: "Foreclosure prevention counseling, rental and eviction help, homelessness assistance, and one-on-one budget or credit sessions at a HUD-approved agency. Agencies are funded by grants specifically so this help costs you nothing.",
    },
    {
      label: "Sometimes a modest fee",
      text: "Group homebuyer education courses and certificates required by some down payment assistance programs can carry a fee — usually $25 to $125. HUD rules require agencies to disclose any fee up front, in writing, and to waive it if you can't afford it.",
    },
    {
      label: "Never a fee",
      text: "You should never pay for a referral, for a loan modification application, or for a promise of a specific outcome. That is not counseling.",
    },
    {
      label: "Agency vs. counselor",
      text: "A HUD-approved agency is the organization HUD has vetted and funded. A HUD-certified counselor is an individual who passed HUD's certification exam. You want both: a certified counselor working at an approved agency. Ask directly — it's a normal question.",
    },
  ],
};

export type ResourceCard = {
  id: string;
  name: string;
  href: string;
  what: string;
  bestFor: string;
  steps: string[];
};

export const SEARCH_RESOURCES: ResourceCard[] = [
  {
    id: "hud",
    name: "HUD housing counseling agency search",
    href: "https://www.hud.gov/i_want_to/talk_to_a_housing_counselor",
    what: "The official federal directory of every HUD-approved agency in the country.",
    bestFor: "Anyone starting from scratch. This is the list that defines 'HUD-approved.'",
    steps: [
      "Open the HUD counselor search and choose search by ZIP code or by state.",
      "Filter by the services you need — foreclosure, pre-purchase, rental, or reverse mortgage.",
      "Note two or three agencies, not one. Availability varies week to week.",
      "Call and confirm the counselor you'll meet with is HUD-certified and the session is free.",
    ],
  },
  {
    id: "cfpb",
    name: "CFPB find-a-counselor tool",
    href: "https://www.consumerfinance.gov/find-a-housing-counselor/",
    what: "The Consumer Financial Protection Bureau's view of the same HUD-approved list, with a cleaner interface and distance sorting.",
    bestFor: "Comparing several nearby agencies quickly, or when the HUD site is slow.",
    steps: [
      "Enter your ZIP code and review results sorted by distance.",
      "Expand an agency to see languages spoken and the exact services offered.",
      "Cross-check any agency you find elsewhere against this list before engaging.",
    ],
  },
  {
    id: "neighborworks",
    name: "NeighborWorks America network",
    href: "https://www.neighborworks.org/",
    what: "A national network of local nonprofits, many of which are also HUD-approved and run down payment assistance programs directly.",
    bestFor: "Filling local gaps when the nearest HUD agency is far away, or when you want help and money from the same place.",
    steps: [
      "Use the network directory to find the affiliate serving your county.",
      "Ask whether they administer local down payment or repair grants in addition to counseling.",
    ],
  },
  {
    id: "hfa",
    name: "Your state housing finance agency",
    href: "https://www.ncsha.org/housing-help/",
    what: "Every state has an HFA that funds first-time buyer programs and maintains its own list of approved education providers.",
    bestFor: "When a down payment assistance program requires a specific certificate that a generic course won't satisfy.",
    steps: [
      "Find your state's HFA and open its homebuyer education page.",
      "Confirm which provider's certificate the program you want will accept before you pay for or complete a course.",
    ],
  },
];

export type Hotline = {
  id: string;
  name: string;
  phone: string;
  tel: string;
  serves: string;
  hours: string;
  urgent?: boolean;
};

export const HOTLINES: Hotline[] = [
  {
    id: "hud_referral",
    name: "HUD housing counseling referral line",
    phone: "(800) 569-4287",
    tel: "+18005694287",
    serves:
      "Anyone who wants to be connected to the nearest HUD-approved agency — buyers, renters, homeowners, and reverse mortgage borrowers. This is a referral line, not a counseling session.",
    hours: "Automated 24/7; live referrals during business hours.",
  },
  {
    id: "hope_hotline",
    name: "Homeowner's HOPE Hotline (995-HOPE)",
    phone: "(888) 995-4673",
    tel: "+18889954673",
    serves:
      "Homeowners who are behind on payments, facing foreclosure, or expecting to miss a payment soon. You are connected to a HUD-approved counselor on the call, free, in English and Spanish with interpretation for other languages.",
    hours: "24 hours a day, 7 days a week.",
    urgent: true,
  },
];

export const URGENT_NOTE =
  "If you have received a notice of default, a foreclosure filing, or a sale date, call the HOPE hotline first and keep talking to your loan servicer at the same time. Counseling does not pause a foreclosure by itself, but a counselor can request time, document your file, and submit a loss-mitigation package the servicer must review. The earlier you call, the more options remain open.";

export type Specialty = {
  id: string;
  need: string;
  title: string;
  text: string;
};

export const SPECIALTIES: Specialty[] = [
  {
    id: "foreclosure",
    need: "foreclosure",
    title: "Foreclosure and delinquency",
    text: "Ask for a foreclosure prevention or loss-mitigation counselor. They review your servicer's options — forbearance, repayment plan, modification, partial claim — and submit the package on your behalf. Always free.",
  },
  {
    id: "pre_purchase",
    need: "pre_purchase",
    title: "Pre-purchase and first-time buyer",
    text: "Covers budget, credit, loan programs, and the certificate many assistance programs require. Confirm the certificate is accepted by your specific program before enrolling.",
  },
  {
    id: "rental",
    need: "rental",
    title: "Rental and eviction",
    text: "Tenant rights, rental assistance applications, lease disputes, and referrals to local emergency rent funds.",
  },
  {
    id: "reverse",
    need: "reverse",
    title: "Reverse mortgage (HECM)",
    text: "A HUD-approved HECM counseling session is legally required before you can close a reverse mortgage. Only counselors on HUD's HECM roster can issue that certificate.",
  },
  {
    id: "credit_budget",
    need: "credit_budget",
    title: "Credit and budget repair",
    text: "One-on-one sessions to build a payment plan, dispute errors, and set a realistic savings target. Distinct from for-profit credit repair, which charges for the same work.",
  },
  {
    id: "language",
    need: "language",
    title: "Language access",
    text: "Both the HUD and CFPB directories list the languages each agency speaks. Federally funded agencies must provide meaningful access to people with limited English proficiency, including free interpretation — you do not have to bring your own translator.",
  },
];

export const SCAM_FLAGS = [
  {
    id: "deed_transfer",
    flag: "They ask you to sign over your deed or transfer title",
    why: "No legitimate counselor ever needs ownership of your home. Deed transfer is the core move in equity-stripping fraud — you keep the debt and lose the house.",
  },
  {
    id: "stop_lender_contact",
    flag: "They tell you to stop talking to your lender or servicer",
    why: "Cutting off servicer contact destroys your loss-mitigation options and lets the foreclosure clock run uninterrupted. A real counselor works with your servicer, not around it.",
  },
  {
    id: "upfront_payment",
    flag: "They demand a large upfront payment, especially by wire, gift card, or crypto",
    why: "Advance fees for loan modification help are illegal in most circumstances, and untraceable payment methods exist to prevent recovery. HUD-approved foreclosure counseling is free.",
  },
  {
    id: "guarantee",
    flag: "They guarantee a specific result",
    why: "No one can promise a modification approval or a stopped sale. Guarantees are a sales tactic, not a service.",
  },
  {
    id: "redirect_payments",
    flag: "They ask you to send mortgage payments to them instead of your servicer",
    why: "Your loan goes unpaid while they collect. This accelerates foreclosure rather than preventing it.",
  },
  {
    id: "pressure_signing",
    flag: "They pressure you to sign documents you haven't read or can't keep a copy of",
    why: "Legitimate agencies give you time, written disclosures, and copies of everything you sign.",
  },
];

export const SCAM_NEXT_STEPS = [
  "Stop communicating and do not send money or documents.",
  "Verify the agency against the HUD or CFPB directory — if it isn't listed, it isn't HUD-approved.",
  "Report it to the CFPB at consumerfinance.gov/complaint and to your state attorney general.",
  "Call the HOPE hotline to get connected with a real counselor immediately.",
];

export const FAQS = [
  {
    id: "cost",
    q: "Is HUD-approved housing counseling really free?",
    a: "Foreclosure prevention, rental, and one-on-one budget counseling at a HUD-approved agency are free. Group homebuyer education courses that issue a certificate can carry a modest fee, typically $25 to $125, and the agency must disclose it in writing before you enroll and waive it if you can't afford it. You should never pay for a referral or for help applying for a loan modification.",
  },
  {
    id: "certification",
    q: "What's the difference between a HUD-approved agency and a HUD-certified counselor?",
    a: "A HUD-approved agency is an organization HUD has vetted, funded, and listed in its official directory. A HUD-certified counselor is an individual who passed HUD's national certification exam. Federal rules require counseling delivered under HUD programs to be provided by certified counselors working at approved agencies. It is reasonable to ask for both before your first session.",
  },
  {
    id: "language",
    q: "Can I get counseling in a language other than English?",
    a: "Yes. Both the HUD and CFPB directories list the languages each agency speaks, and you can filter for them. Federally funded agencies are required to provide meaningful access to people with limited English proficiency, which includes free interpretation services. The HOPE hotline offers English and Spanish counselors with interpretation available for other languages.",
  },
  {
    id: "specialties",
    q: "What kinds of problems can a housing counselor actually help with?",
    a: "Pre-purchase preparation and homebuyer education certificates, down payment assistance applications, foreclosure prevention and loss mitigation, rental and eviction issues, reverse mortgage (HECM) counseling, post-purchase and home repair questions, and credit and budget planning. Not every agency covers every category, so filter by the service you need when you search.",
  },
  {
    id: "verify",
    q: "How do I verify an agency is legitimate?",
    a: "Look it up by name in HUD's official directory or the CFPB find-a-counselor tool. If it does not appear in either, it is not HUD-approved, regardless of what its website claims. Then confirm by phone that the counseling is free, ask whether your counselor is HUD-certified, and request written disclosure of any fee before you share financial documents.",
  },
  {
    id: "urgent",
    q: "Foreclosure is imminent — what do I do first?",
    a: "Call the Homeowner's HOPE Hotline at (888) 995-4673. It is staffed 24/7 and connects you to a HUD-approved counselor on the same call at no cost. Keep responding to your servicer while you do this, and gather your loan number, recent income documentation, and any notices you've received. Counseling does not automatically stop a sale, but a counselor can submit a loss-mitigation package your servicer is required to review.",
  },
];

export const DISCLAIMER =
  "Home Approach is not a HUD-approved housing counseling agency and does not provide counseling. This page is educational and points to the official federal directories and hotlines. Fee rules, hotline hours, and agency availability can change — verify directly with HUD or the agency.";

export const HOUSING_COUNSELOR_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
